import { Helmet } from "react-helmet";
import { useState, useEffect } from "react";
import {
  FaFingerprint,
  FaRightToBracket,
  FaRightFromBracket,
  FaClock,
  FaLocationDot,
  FaCheckDouble,
  FaTriangleExclamation,
  FaChevronLeft,
  FaChevronRight,
  FaUserPlus,
} from "react-icons/fa6";
import { Header } from "../../../components/Header";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { useHookPresence } from "../../../hooks/useHookPresence";
import { BtnDefault } from "../../../components/buttons/BtnDefault";
import { useNavigate } from "react-router-dom";

/* ============================================
   HELPER FUNCTIONS (Local Utilities)
   ============================================ */
const formatTimeWIB = (date) => {
  return new Date(date).toLocaleTimeString("id-ID", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatDateIndo = (dateString) => {
  const options = { day: "numeric", month: "short", year: "numeric" };
  return new Date(dateString).toLocaleDateString("id-ID", options);
};

const formatDuration = (ms) => {
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor((ms / (1000 * 60 * 60)));

  const pad = (num) => num.toString().padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/* ============================================
   KOMPONEN: Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 6 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3.5 bg-slate-700/60 rounded-md"
          style={{ width: `${40 + Math.random() * 50}%` }}
        />
      </td>
    ))}
  </tr>
);

/* ============================================
   KOMPONEN: Status Badge
   ============================================ */
const StatusBadge = ({ status }) => {
  const config = {
    "Tepat Waktu": {
      bg: "bg-emerald-500/12",
      text: "text-emerald-400",
      border: "border-emerald-500/20",
      dot: "bg-emerald-400",
    },
    Terlambat: {
      bg: "bg-amber-500/12",
      text: "text-amber-400",
      border: "border-amber-500/20",
      dot: "bg-amber-400",
    },
    Izin: {
      bg: "bg-blue-500/12",
      text: "text-blue-400",
      border: "border-blue-500/20",
      dot: "bg-blue-400",
    },
    Alpha: {
      bg: "bg-red-500/12",
      text: "text-red-400",
      border: "border-red-500/20",
      dot: "bg-red-400",
    },
  };

  const c = config[status] || config.Alpha;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${c.bg} ${c.text} border ${c.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {status || "Alpha"}
    </span>
  );
};

/* ============================================
   KOMPONEN: Toast Notification
   ============================================ */
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === "success" ? "border-emerald-500/30 bg-emerald-900/80" : "border-red-500/30 bg-red-900/80";
  const icon = type === "success" ? <FaCheckDouble className="text-emerald-400" /> : <FaTriangleExclamation className="text-red-400" />;

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border backdrop-blur-md text-white shadow-lg ${bgColor}`}
      style={{ animation: "slideIn 0.3s ease-out" }}
    >
      {icon}
      <span className="text-sm font-medium">{message}</span>
    </div>
  );
};

/* ============================================
   KOMPONEN: Presence (Main)
   ============================================ */
export const Presence = () => {
  const navigate = useNavigate();
  /* ---------- Sidebar State ---------- */
  const [showSidebar, setShowSidebar] = useState(false);

  /* ---------- Clock & Time State ---------- */
  const [currentTime, setCurrentTime] = useState(new Date());
  const [workDuration, setWorkDuration] = useState("00:00:00");

  /* ---------- Toast State ---------- */
  const [toast, setToast] = useState(null);

  /* ---------- Hook Integration ---------- */
  const {
    data,
    meta,
    loading,
    activeStatus,
    monthlyStats,
    loadingAction,
    params,
    setParams,
    handleClockIn,
    handleClockOut,
  } = useHookPresence();

  // Extract state dari hook agar lebih mudah dibaca
  const isClockedIn = activeStatus.is_clocked_in;
  const clockInTime = activeStatus.clock_in_time;

  /* ---------- Effects ---------- */
  // Update Jam Real-time & Hitung Durasi Kerja
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);

      // Jika sedang bekerja, hitung durasi dari waktu clock in
      if (isClockedIn && clockInTime) {
        const diff = now - new Date(clockInTime);
        setWorkDuration(formatDuration(diff));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isClockedIn, clockInTime]);

  // Reset durasi jika selesai bekerja
  useEffect(() => {
    if (!isClockedIn) {
      setWorkDuration("00:00:00");
    }
  }, [isClockedIn]);

  /* ---------- Handlers ---------- */
  const onClockIn = async () => {
    try {
      await handleClockIn({ location: "Kantor Pusat (Deteksi Otomatis)" });
      setToast({ message: "Berhasil Absen Masuk! Semangat bekerja.", type: "success" });
    } catch (err) {
      setToast({ 
        message: err?.response?.data?.message || "Gagal melakukan absen masuk.", 
        type: "error" 
      });
    }
  };

  const onClockOut = async () => {
    try {
      await handleClockOut({ location: "Kantor Pusat (Deteksi Otomatis)" });
      setToast({ message: "Berhasil Absen Keluar. Hati-hati di jalan!", type: "success" });
    } catch (err) {
      setToast({ 
        message: err?.response?.data?.message || "Gagal melakukan absen keluar.", 
        type: "error" 
      });
    }
  };

  /* ---------- Column Config ---------- */
  const columns = [
    { key: "date", label: "Tanggal", align: "left" },
    { key: "in", label: "Masuk", align: "left", width: "100px" },
    { key: "out", label: "Keluar", align: "left", width: "100px" },
    { key: "total", label: "Total Kerja", align: "left" },
    { key: "status", label: "Status", align: "left", width: "130px" },
  ];

  return (
    <>
      <Helmet>
        <title>Absensi Karyawan - DANELA NET</title>
        <meta
          name="description"
          content="Halaman absensi karyawan, pencatatan kehadiran masuk dan keluar."
        />
      </Helmet>

      <DashboardLayout
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
      >
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <FaFingerprint className="text-cyan-400 text-lg" />
            <h2 className="text-base md:text-xl font-bold text-gray-100">
              Absensi Karyawan
            </h2>
          </div>

          <BtnDefault 
          onClick={() => navigate("/dashboard/presence/form")}
          >
            <span className="hidden lg:flex items-center gap-2">
              <FaUserPlus className="text-xs" />
              Tambah Data
            </span>
            <FaUserPlus className="lg:hidden" />
          </BtnDefault>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          
          {/* ===== Hero Section (Clock & Actions) ===== */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Clock Card */}
            <div
              className="rounded-2xl p-6 flex flex-col justify-center items-center relative overflow-hidden"
              style={{
                background: "rgba(15, 23, 42, 0.5)",
                border: "1px solid rgba(51, 65, 85, 0.3)",
                backdropFilter: "blur(12px)",
              }}
            >
              <div
                className="absolute w-48 h-48 bg-cyan-500/20 rounded-full blur-[80px] -z-0"
                style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
              />
              <h1 
                className="text-5xl lg:text-6xl font-bold text-white tracking-tighter z-10" 
                style={{ textShadow: "0 0 20px rgba(6,182,212,0.3)" }}
              >
                {formatTimeWIB(currentTime)}
              </h1>
              <p className="text-slate-400 mt-2 text-sm lg:text-base z-10">
                {new Date(currentTime).toLocaleDateString("id-ID", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            </div>

            {/* Action Card */}
            <div
              className="rounded-2xl p-6 flex flex-col justify-between"
              style={{
                background: "rgba(15, 23, 42, 0.5)",
                border: "1px solid rgba(51, 65, 85, 0.3)",
                backdropFilter: "blur(12px)",
              }}
            >
              {/* Status Indicator */}
              <div className="flex justify-between items-center mb-6">
                <div
                  className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold border ${
                    isClockedIn
                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                      : "bg-slate-700/30 text-slate-400 border-slate-600/30"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      isClockedIn ? "bg-emerald-400 animate-pulse" : "bg-slate-500"
                    }`}
                  />
                  {isClockedIn ? "Sedang Bekerja" : "Belum Absen"}
                </div>
                
                {/* Live Timer if working */}
                {isClockedIn && (
                  <div className="font-mono text-xl text-cyan-400 font-bold">
                    {workDuration}
                  </div>
                )}
              </div>

              {/* Location Info */}
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-8">
                <FaLocationDot />
                <span>{activeStatus?.data?.location || "Kantor Pusat (Deteksi Otomatis)"}</span>
              </div>

              {/* Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={onClockIn}
                  disabled={isClockedIn || loadingAction}
                  className={`flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 ${
                    isClockedIn
                      ? "bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700"
                      : "bg-cyan-600 text-white hover:bg-cyan-500 shadow-lg shadow-cyan-600/20"
                  }`}
                >
                  {loadingAction && !isClockedIn ? (
                    <i className="fa-solid fa-spinner fa-spin" />
                  ) : (
                    <FaRightToBracket />
                  )}
                  {isClockedIn ? "Sudah Masuk" : "Absen Masuk"}
                </button>

                <button
                  onClick={onClockOut}
                  disabled={!isClockedIn || loadingAction}
                  className={`flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 ${
                    !isClockedIn
                      ? "bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700"
                      : "bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
                  }`}
                >
                  {loadingAction && isClockedIn ? (
                    <i className="fa-solid fa-spinner fa-spin" />
                  ) : (
                    <FaRightFromBracket />
                  )}
                  Absen Keluar
                </button>
              </div>
            </div>
          </section>

          {/* ===== Stats Grid ===== */}
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* Stat 1: Total Hari Ini */}
            <div
              className="rounded-2xl p-5 flex items-center gap-4"
              style={{
                background: "rgba(15, 23, 42, 0.5)",
                border: "1px solid rgba(51, 65, 85, 0.3)",
              }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-cyan-500/10 text-cyan-400">
                <FaClock className="text-xl" />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase font-semibold tracking-wider">Total Hari Ini</p>
                <p className="text-xl font-bold text-white mt-1">
                  {isClockedIn ? workDuration : "-"}
                </p>
              </div>
            </div>

            {/* Stat 2: Kehadiran Bulan Ini */}
            <div
              className="rounded-2xl p-5 flex items-center gap-4"
              style={{
                background: "rgba(15, 23, 42, 0.5)",
                border: "1px solid rgba(51, 65, 85, 0.3)",
              }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald-500/10 text-emerald-400">
                <FaCheckDouble className="text-xl" />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase font-semibold tracking-wider">Kehadiran Bulan Ini</p>
                <p className="text-xl font-bold text-white mt-1">
                  {monthlyStats.kehadiran} Hari
                </p>
              </div>
            </div>

            {/* Stat 3: Terlambat */}
            <div
              className="rounded-2xl p-5 flex items-center gap-4"
              style={{
                background: "rgba(15, 23, 42, 0.5)",
                border: "1px solid rgba(51, 65, 85, 0.3)",
              }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-500/10 text-amber-400">
                <FaTriangleExclamation className="text-xl" />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase font-semibold tracking-wider">Terlambat</p>
                <p className="text-xl font-bold text-white mt-1">
                  {monthlyStats.terlambat} Kali
                </p>
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr
                    className="border-b"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderColor: "rgba(51, 65, 85, 0.3)",
                    }}
                  >
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${
                          col.align === "center" ? "text-center" : "text-left"
                        }`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody
                  className="divide-y"
                  style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                >
                  {loading && (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} cols={columns.length} />
                      ))}
                    </>
                  )}

                  {!loading && data?.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{
                              background: "rgba(51, 65, 85, 0.2)",
                              border: "1px solid rgba(51, 65, 85, 0.2)",
                            }}
                          >
                            <FaFingerprint className="text-2xl text-slate-600" />
                          </div>
                          <p className="text-slate-400 font-medium">Belum ada riwayat absensi</p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    data?.map((item) => (
                      <tr
                        key={item.id}
                        className="transition-colors duration-150 hover:bg-slate-800/30"
                      >
                        <td className="px-4 py-3">
                          <span className="text-gray-100 font-medium">
                            {formatDateIndo(item.date)}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-cyan-400/80 text-xs">
                          {item.in || "-"}
                        </td>
                        <td className="px-4 py-3 font-mono text-slate-400 text-xs">
                          {item.out || "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-300 font-medium">
                          {item.total || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={item.status} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Desktop */}
            {meta && meta.last_page > 1 && (
              <div
                className="flex items-center justify-between px-5 py-3.5"
                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}
              >
                <p className="text-xs text-slate-500">
                  Menampilkan{" "}
                  <span className="text-slate-300 font-medium">{meta.from}</span> -{" "}
                  <span className="text-slate-300 font-medium">{meta.to}</span> dari{" "}
                  <span className="text-slate-300 font-medium">{meta.total}</span>
                </p>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setParams((p) => ({ ...p, page: meta.current_page - 1 }))}
                    disabled={meta.current_page === 1}
                    className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <FaChevronLeft className="text-xs" />
                  </button>

                  {Array.from({ length: meta.last_page }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setParams((prev) => ({ ...prev, page: p }))}
                      className={`min-w-[34px] h-9 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        meta.current_page === p
                          ? "bg-cyan-600 text-white shadow-lg shadow-cyan-600/25"
                          : "text-slate-400 hover:text-gray-100 hover:bg-slate-700/40"
                      }`}
                    >
                      {p}
                    </button>
                  ))}

                  <button
                    onClick={() => setParams((p) => ({ ...p, page: meta.current_page + 1 }))}
                    disabled={meta.current_page === meta.last_page}
                    className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <FaChevronRight className="text-xs" />
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* ===== Mobile Card List ===== */}
          <section className="lg:hidden space-y-3">
            {loading && (
              <>
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="rounded-2xl overflow-hidden animate-pulse" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                    <div className="px-4 py-3 flex justify-between" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.2)" }}>
                      <div className="h-4 bg-slate-700/60 rounded-md w-28" />
                      <div className="h-6 bg-slate-700/40 rounded-full w-20" />
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-4">
                      <div className="h-4 bg-slate-700/60 rounded-md w-20" />
                      <div className="h-4 bg-slate-700/60 rounded-md w-20" />
                    </div>
                  </div>
                ))}
              </>
            )}

            {!loading && data?.length === 0 && (
              <div className="rounded-2xl p-12 text-center" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                <FaFingerprint className="text-3xl text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 font-medium text-sm">Belum ada riwayat absensi</p>
              </div>
            )}

            {!loading &&
              data?.map((item) => (
                <article
                  key={item.id}
                  className="rounded-2xl overflow-hidden"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                >
                  <div
                    className="px-4 py-3 flex justify-between items-center border-b"
                    style={{ borderColor: "rgba(51, 65, 85, 0.2)" }}
                  >
                    <span className="text-sm font-bold text-slate-300">
                      {formatDateIndo(item.date)}
                    </span>
                    <StatusBadge status={item.status} />
                  </div>
                  <div className="p-4 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[11px] text-slate-500 uppercase font-bold mb-1">Masuk</p>
                      <p className="text-cyan-400 font-mono text-sm">{item.in || "-"}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500 uppercase font-bold mb-1">Keluar</p>
                      <p className="text-slate-300 font-mono text-sm">{item.out || "-"}</p>
                    </div>
                    <div className="col-span-2 pt-2 border-t border-slate-700/30 flex justify-between items-center">
                      <span className="text-xs text-slate-400">Total: {item.total || "-"}</span>
                    </div>
                  </div>
                </article>
              ))}

            {/* Pagination Mobile */}
            {meta && meta.last_page > 1 && (
              <div className="flex items-center justify-between pt-3 pb-6 px-1" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
                <button
                  onClick={() => setParams((p) => ({ ...p, page: meta.current_page - 1 }))}
                  disabled={meta.current_page === 1}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
                >
                  <FaChevronLeft className="text-xs" />
                  Sebelumnya
                </button>

                <span className="text-sm font-medium text-slate-400">
                  <span className="text-cyan-400">{meta.current_page}</span>
                  <span className="text-slate-600 mx-1">/</span>
                  {meta.last_page}
                </span>

                <button
                  onClick={() => setParams((p) => ({ ...p, page: meta.current_page + 1 }))}
                  disabled={meta.current_page === meta.last_page}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
                >
                  Berikutnya
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </section>
        </main>
      </DashboardLayout>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};